import { reportKeywordStyle } from '../utils/reportKeywords';
import { formatReportBodyDisplay, reportHeadingTone } from '../utils/reportBodyDisplay';
import { decodeNexusMarkup, NEXUS_DROP_TAGS } from '../utils/nexusMarkup';
import React, { useEffect, useMemo, useState } from 'react';
import { sectionId } from '../utils/phase5ReportUi';
import { formatReportFieldLabelsHtml, isReportFieldLabelText } from '../utils/reportFieldFormatting';
import { getReportHeadingPresentation, Phase5SectionHeading, phase5Tone } from './Phase5ReportSystem';

// Every body path uses the same sanitized presentation tree. Prefixes and
// labels remain a single inline token; no body content is reassigned.
const inlineMarkdown = (text, keyPrefix) => {
  const doc = new DOMParser().parseFromString(formatReportBodyDisplay(formatReportFieldLabelsHtml(text)), 'text/html');
  return safeNodeChildren(doc.body, keyPrefix, '', true);
};

export const renderNexusMarkdownLite = (text, keyPrefix = 'nexus', preserveSourceText = false) => {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let bullets = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    const listIndex = blocks.length;
    blocks.push(
      <ul key={`${keyPrefix}-ul-${listIndex}`}>
        {bullets.map((item, index) => (
          <li key={`${keyPrefix}-li-${listIndex}-${index}`}>
            {inlineMarkdown(item, `${keyPrefix}-li-${listIndex}-${index}`)}
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) {
      flushBullets();
      return;
    }
    const bullet = line.match(/^[-•]\s+(.+)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flushBullets();
    const wholeBold = line.match(/^\*\*([^*\n]+)\*\*$/);
    const objectionHeading = line.match(/^(Objection\s+\d+\s*:\s*.+)$/i);
    blocks.push((wholeBold && !isReportFieldLabelText(wholeBold[1])) || objectionHeading
      ? <p data-report-tone={reportHeadingTone((wholeBold || objectionHeading)[1])} className="nexus-subheading phase5-subheading" key={`${keyPrefix}-p-${index}`}><strong>{(wholeBold || objectionHeading)[1]}</strong></p>
      : <p className="phase5-body-text" key={`${keyPrefix}-p-${index}`}>{inlineMarkdown(line, `${keyPrefix}-p-${index}`)}</p>);
  });
  flushBullets();
  return blocks;
};

const SAFE_BLOCK_TAGS = new Set(['h3', 'p', 'ul', 'ol', 'li']);
const SAFE_INLINE_TAGS = new Set(['b', 'strong', 'em', 'br']);
const DROP_ENTIRELY_TAGS = NEXUS_DROP_TAGS;

const comparableHeading = (value) => String(value || '')
  .replace(/^\s*\d+(?:\.\d+)*[.)]?\s*/, '')
  .replace(/[“”"'`]/g, '')
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .trim()
  .toLowerCase();

// Nexus stores its parent details as sanitized HTML. Keep its adapter local so
// the secure Nexus renderer does not import the Markdown renderer (and its
// marked dependency). Only complete block headings are boundaries.
const NEXUS_INLINE_LABELS = new Set(['EVIDENCE', 'HYPOTHESIS', 'CLINICAL RELEVANCE', 'BUFFER', 'TRIGGER', 'AMPLIFIER', 'MAINTENANCE CYCLE', 'POINT CITATIONS', 'DISORDER-RELEVANT STRUCTURAL NOTE']);
export const splitNexusSubsections = (source, parentId, parentTitle = '') => {
  const lines = String(source || '').replace(/\r\n?/g, '\n').replace(/<\/(?:p|h[3-6]|div)>\s*</gi, (match) => `${match.slice(0, -1)}\n<`).split('\n');
  const markers = [];
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const match = trimmed.match(/^<(?:h[3-6]|p|div)\b[^>]*>\s*(?:<strong|<b)\b[^>]*>([\s\S]*?)<\/(?:strong|b)>\s*<\/(?:h[3-6]|p|div)>$/i)
      || trimmed.match(/^<(?:h[3-6])\b[^>]*>\s*([\s\S]*?)\s*<\/(?:h[3-6])>$/i)
      || trimmed.match(/^\*\*([^*]+)\*\*$/)
      || trimmed.match(/^((?:[A-Z]\.|\d+(?:\.\d+)*[.)]?)\s+.+)$/)
      || trimmed.match(/^([A-Z][A-Z0-9 &/,:–—()'\-]{4,})$/);
    const title = String(match?.[1] || '').replace(/<[^>]*>/g, '').replace(/\*+/g, '').replace(/\s+/g, ' ').trim().replace(/:$/, '').trim();
    const upper = title.toUpperCase();
    const lettered = /^[A-Z]\.[ \t]+/.test(title);
    const numbered = /^\d+(?:\.\d+)*[.)]?\s+[A-Z0-9][A-Z0-9 &/,:–—()'\-]+$/.test(title);
    const named = /^(?:PATTERN|EXPLORATION|ANOMALY|INCONGRUENCE|INTERPRETATION|AREA)\b/i.test(title);
    if (match && title.length >= 4 && comparableHeading(title) !== comparableHeading(parentTitle)
      && !NEXUS_INLINE_LABELS.has(upper) && (lettered || numbered || named || (title === upper && title.split(/\s+/).length >= 2))) markers.push({ index, title: title + (String(match[1]).replace(/<[^>]*>/g, '').replace(/\*/g, '').trim().endsWith(':') ? ':' : '') });
  });
  if (!markers.length) return null;
  return { body: lines.slice(0, markers[0].index).join('\n'), internalSections: markers.map((marker, index) => ({ id: `${parentId}-internal-${index + 1}`, title: marker.title, body: lines.slice(marker.index + 1, markers[index + 1]?.index ?? lines.length).join('\n') })) };
};

const safeNodeChildren = (node, keyPrefix, sectionTitle, preserveSourceText) => Array.from(node.childNodes || [])
  .flatMap((child, index) => safeHtmlNode(child, `${keyPrefix}-${index}`, sectionTitle, preserveSourceText));

const safeHtmlNode = (node, keyPrefix, sectionTitle, preserveSourceText) => {
  if (node.nodeType === 3) {
    const value = node.nodeValue || '';
    return [<React.Fragment key={keyPrefix}>{value}</React.Fragment>];

  }
  if (node.nodeType !== 1) return [];
  const tag = node.tagName.toLowerCase();
  if (DROP_ENTIRELY_TAGS.has(tag)) return [];
  const children = safeNodeChildren(node, keyPrefix, sectionTitle, preserveSourceText);
  if (node.classList.contains('report-user-response') || node.classList.contains('report-reference')) {
    return [<span className={node.className} key={keyPrefix}>{children}</span>];
  }
  if (tag === 'a' && /^(?:https?:|#)/i.test(node.getAttribute('href') || '')) return [<a href={node.getAttribute('href')} key={keyPrefix}>{children}</a>];
  if (node.classList.contains('report-keyword')) {
    const index = node.getAttribute('data-report-keyword');
    return [<span className="report-keyword" data-report-keyword={index} style={reportKeywordStyle(index)} key={keyPrefix}>{children}</span>];
  }
  if (node.classList.contains('report-sentence-space') || node.classList.contains('report-sentence-continuation') || node.classList.contains('report-field') || node.classList.contains('report-field-body') || node.classList.contains('report-field-space')) {
    return [<span className={node.className} key={keyPrefix}>{children}</span>];
  }
  if (node.classList.contains('phase5-field-label')) {
    return [<span className={node.className} data-report-tone={node.getAttribute("data-report-tone")} key={keyPrefix}>{node.textContent}</span>];
  }
  // Apart from the allowlisted reference links above, source attributes are
  // ignored; generated emphasis uses only the shared configured colours.
  if (tag === 'br') return [<br key={keyPrefix} />];
  // Qualification (including quotation protection) belongs to the shared
  // formatter. Never promote a strong node rejected by that formatter here.
  if (tag === 'b' || tag === 'strong') return [<strong key={keyPrefix}>{children}</strong>];
  if (tag === 'em' || tag === 'i') return [<em key={keyPrefix}>{children}</em>];
  if (tag === 'h3') {
    if (comparableHeading(node.textContent) === comparableHeading(sectionTitle)) return [];
    return [<h3 data-report-tone={reportHeadingTone(node.textContent)} className="nexus-content-heading" key={keyPrefix}>{children}</h3>];
  }
  if (tag === 'p') {
    return [<p className="phase5-body-text" key={keyPrefix}>{children}</p>];
  }
  if (tag === 'ul') return [<ul key={keyPrefix}>{children}</ul>];
  if (tag === 'ol') return [<ol start={node.hasAttribute('start') ? Number(node.getAttribute('start')) : undefined} key={keyPrefix}>{children}</ol>];
  if (tag === 'li') return [<li className={node.classList.contains('phase5-field-list-item') ? 'phase5-field-list-item' : undefined} key={keyPrefix}>{children}</li>];
  // Unknown inert formatting tags are stripped while their text is retained.
  return children;
};

export const renderNexusHtmlLite = (text, sectionTitle, keyPrefix = 'nexus-html', preserveSourceText = false, sourceResponses = []) => {
  const decoded = decodeNexusMarkup(text);
  if (!preserveSourceText && !/<\/?[a-z][\s\S]*?>/i.test(decoded)) return renderNexusMarkdownLite(decoded, keyPrefix, false);
  const value = formatReportBodyDisplay(formatReportFieldLabelsHtml(decoded), sourceResponses);
  const inert = new DOMParser().parseFromString(value, 'text/html');
  const hasApprovedMarkup = Array.from(inert.body.querySelectorAll('*')).some((node) => (
    SAFE_BLOCK_TAGS.has(node.tagName.toLowerCase()) || SAFE_INLINE_TAGS.has(node.tagName.toLowerCase())
  ));
  if (!hasApprovedMarkup) return safeNodeChildren(inert.body, keyPrefix, sectionTitle, preserveSourceText);
  return safeNodeChildren(inert.body, keyPrefix, sectionTitle, preserveSourceText);
};

const parseSections = (content, preserveSourceText = false) => {
  if (!content || typeof DOMParser === 'undefined') return [];
  const documentNode = new DOMParser().parseFromString(String(content), 'text/html');
  const details = Array.from(documentNode.body.querySelectorAll('details'));
  if (!details.length) return [{ title: null, bodies: [documentNode.body.innerHTML || ''] }];
  return details.map((detail) => {
    const summary = detail.querySelector(':scope > summary');
    const bodies = Array.from(detail.children)
      .filter((child) => child !== summary && child.tagName.toLowerCase() !== 'details')
      .map((child) => preserveSourceText || child.children.length ? child.outerHTML : child.textContent || '');
    return {
      title: summary?.textContent?.trim() || '',
      bodies: bodies.length ? bodies : [detail.textContent?.replace(summary?.textContent || '', '') || ''],
    };
  });
};

const NexusReportRenderer = ({ sourceResponses = [], content, testId, preserveSourceText = false, expandedSectionIds = [], onExpandedSectionIdsChange, onSectionIdsChange }) => {
  const sections = useMemo(() => parseSections(content, preserveSourceText), [content, preserveSourceText]);
  const identifiedSections = useMemo(() => {
    const counts = {};
    return sections.map((section) => {
      const base = sectionId(section.title);
      const id = sectionId(section.title, counts[base] || 0);
      counts[base] = (counts[base] || 0) + 1;
      const rawBody = decodeNexusMarkup(section.bodies.join('\n'));
      const nested = splitNexusSubsections(rawBody, id, section.title);
      return { ...section, id, nested: nested?.internalSections?.length ? nested : null };
    });
  }, [sections]);
  const available = new Set(identifiedSections.filter((section) => section.title).map((section) => section.id));
  const availableKey = [...available].join('\u0000');
  const expanded = new Set(expandedSectionIds.filter((id) => available.has(id)));
  const nestedIds = identifiedSections.flatMap((section) => (section.nested?.internalSections || []).map((item) => item.id));
  const [expandedNested, setExpandedNested] = useState(() => new Set());
  const nestedKey = nestedIds.join('\u0000');
  useEffect(() => { setExpandedNested(new Set()); }, [nestedKey]);
  useEffect(() => {
    onSectionIdsChange?.([...available]);
  }, [availableKey, onSectionIdsChange]);
  const toggle = (id, open) => {
    const next = new Set(expanded);
    if (open) next.add(id); else next.delete(id);
    onExpandedSectionIdsChange?.([...next]);
  };
  if (!content) return null;

  return (
    <div className="phase5-report rich-report-renderer nexus-report-renderer text-gray-800" data-testid={testId}>
      {identifiedSections.map((section, sectionIndex) => {
        const heading = getReportHeadingPresentation(section.title, sectionIndex + 1);
        return section.title ? (
        <details key={section.id} className={`phase5-report-section ${phase5Tone(sectionIndex)}`} open={expanded.has(section.id)}>
          <summary onClick={(event) => {
            event.preventDefault();
            toggle(section.id, !expanded.has(section.id));
          }}><Phase5SectionHeading index={sectionIndex} displayNumber={heading.number}>{heading.title}</Phase5SectionHeading></summary>
          <div className="phase5-section-body nexus-section-body">
            {section.nested ? <>
              {section.nested.body && renderNexusHtmlLite(section.nested.body, section.title, `s${sectionIndex}-preamble`, true, sourceResponses)}
              {section.nested.internalSections.map((subsection) => {
                const open = expandedNested.has(subsection.id);
                const controlId = `${subsection.id}-control`;
                const regionId = `${subsection.id}-content`;
                const toggleNested = () => setExpandedNested((current) => {
                  const next = new Set(current); if (next.has(subsection.id)) next.delete(subsection.id); else next.add(subsection.id); return next;
                });
                return <div className="phase5-internal-subsection" key={subsection.id}>
                  <button type="button" id={controlId} className="phase5-internal-subsection-button" data-report-tone={reportHeadingTone(subsection.title)}
                    data-testid={`internal-subsection-${subsection.id}`} aria-expanded={open} aria-controls={regionId}
                    onClick={(event) => { event.stopPropagation(); toggleNested(); }}
                    onKeyDown={(event) => { event.stopPropagation(); if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleNested(); } }}>
                    <span aria-hidden="true" className={`phase5-internal-subsection-chevron${open ? ' is-expanded' : ''}`}>⌄</span>
                    <span>{subsection.title}</span>
                  </button>
                  <div id={regionId} role="region" aria-labelledby={controlId} hidden={!open} className="phase5-internal-subsection-content">
                    {renderNexusHtmlLite(subsection.body, section.title, `${subsection.id}-body`, true, sourceResponses)}
                  </div>
                </div>;
              })}
            </> : section.bodies.flatMap((body, bodyIndex) => renderNexusHtmlLite(body, section.title, `s${sectionIndex}-b${bodyIndex}`, preserveSourceText, sourceResponses))}
          </div>
        </details>
      ) : (
        <div key={`section-${sectionIndex}`} className="nexus-section-body">
          {section.bodies.flatMap((body, bodyIndex) => renderNexusHtmlLite(body, section.title, `s${sectionIndex}-b${bodyIndex}`, preserveSourceText, sourceResponses))}
        </div>
      ); })}
      <style>{`
        .nexus-report-renderer .nexus-section-body p { margin: .45rem 0; }
        .nexus-report-renderer .nexus-section-body .nexus-content-heading { margin: 1rem 0 .45rem; color: #111827; }
        .nexus-report-renderer .nexus-section-body .nexus-subheading { margin-top: .85rem; color: #111827; }
        .nexus-report-renderer .nexus-section-body ul { margin: .45rem 0; padding-left: 1.35rem; list-style: disc; }
        .nexus-report-renderer .nexus-section-body ol { margin: .45rem 0; padding-left: 1.5rem; list-style: decimal; }
        .nexus-report-renderer .nexus-section-body li { padding-left: .15rem; }
      `}</style>
    </div>
  );
};

export default NexusReportRenderer;
